import type { ApiClient } from '../../../lib/api-client.js';
import type {
  WorkfrontApiResult,
  WorkfrontCustomFormDetailsRaw,
  WorkfrontCustomFormItem,
  WorkfrontOperationRequest,
  WorkfrontParameterRaw,
  WorkfrontWorkspaceContext,
} from './types.js';

/**
 * Customer-API client for the workfront custom-forms migration
 * surface. Every operation POSTs to `/v1/workfront/custom-forms`
 * with `{ operation, workspaceRef, ... }`; the engine resolves the
 * license from the JWT and forwards into Workfront's
 * `attask/api/v21.0/*` using the per-license API key.
 *
 * Adapted from `wf-audit/src/slices/workfront/data/custom-forms.api.ts`
 * — same operation taxonomy, two changes:
 *
 *   1. Body no longer carries `licenseId` (JWT does).
 *   2. Network call uses the shared `ApiClient` rather than `fetch`
 *      so silent-refresh + 401 handling come for free.
 *
 * Engine non-2xx responses already pass through with `{ error, detail,
 * status }`; we shape that into the `WorkfrontApiResult` envelope the
 * legacy callers expect.
 */

function extractErrorMessage(payload: unknown): string {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object') {
    const error = (payload as { error?: unknown }).error;
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object') {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string') return message;
    }
    const detail = (payload as { detail?: unknown }).detail;
    if (detail && typeof detail === 'object') {
      const error = (detail as { error?: unknown }).error;
      if (error && typeof error === 'object') {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string') return message;
      }
    }
    const message = (payload as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return 'Workfront request failed';
}

async function callWorkfrontOperation<T>(
  api: ApiClient,
  request: WorkfrontOperationRequest,
): Promise<WorkfrontApiResult<T>> {
  try {
    const data = await api.post<WorkfrontApiResult<T> | T>('/v1/workfront/custom-forms', request);
    // The engine route already wraps successful Workfront responses
    // in the same `{ data: ... }` envelope Workfront uses, so we
    // can pass it through verbatim. If we receive a bare T we
    // wrap it for caller convenience.
    if (data && typeof data === 'object' && ('data' in data || 'error' in data)) {
      return data as WorkfrontApiResult<T>;
    }
    return { data: data as T };
  } catch (err) {
    const message = err instanceof Error ? err.message : extractErrorMessage(err);
    return { error: { message } };
  }
}

export function makeCustomFormsApi(api: ApiClient) {
  return {
    getCustomForms(workspace: WorkfrontWorkspaceContext) {
      return callWorkfrontOperation<WorkfrontCustomFormItem[]>(api, {
        operation: 'getCustomForms',
        ...workspace,
      });
    },

    getCustomFormDetails(workspace: WorkfrontWorkspaceContext, customFormId: string) {
      return callWorkfrontOperation<WorkfrontCustomFormDetailsRaw>(api, {
        operation: 'getCustomFormDetails',
        ...workspace,
        customFormId,
      });
    },

    getMatchedParameterGroups(workspace: WorkfrontWorkspaceContext, params: string) {
      return callWorkfrontOperation<Array<{ ID: string; name: string }>>(api, {
        operation: 'getMatchedParameterGroups',
        ...workspace,
        params,
      });
    },

    getMatchedParameters(workspace: WorkfrontWorkspaceContext, params: string) {
      return callWorkfrontOperation<WorkfrontParameterRaw[]>(api, {
        operation: 'getMatchedParameters',
        ...workspace,
        params,
      });
    },

    copyCustomFormGroups(workspace: WorkfrontWorkspaceContext, params: Record<string, unknown>[]) {
      return callWorkfrontOperation<Array<{ ID: string; name: string }>>(api, {
        operation: 'copyCustomFormGroups',
        ...workspace,
        params,
      });
    },

    copyCustomFormParams(workspace: WorkfrontWorkspaceContext, params: Record<string, unknown>) {
      return callWorkfrontOperation<{ ID: string; name: string }>(api, {
        operation: 'copyCustomFormParams',
        ...workspace,
        params,
      });
    },

    copyCustomForm(
      workspace: WorkfrontWorkspaceContext,
      params: Record<string, unknown>,
      id?: string,
    ) {
      return callWorkfrontOperation<Record<string, unknown>>(api, {
        operation: 'copyCustomForm',
        ...workspace,
        params,
        ...(id ? { id } : {}),
      });
    },
  };
}

export type CustomFormsApi = ReturnType<typeof makeCustomFormsApi>;
